import Seo from "@/components/seo/Seo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useTopicLocale } from "@/hooks/use-topic-locale";
import {
  OZK_TOPICS,
  TOPIC_CATEGORIES,
  getTopicCategoryLabel,
  getTopicDescription,
  getTopicLabel,
  getTopicSecondaryLabel,
  getTopicsByCategory,
  searchTopics,
  type TopicCategoryId,
} from "@shared/topic-taxonomy";
import {
  getTopicProblem,
  getTopicProblemUrgencyLabel,
} from "@shared/topic-problems";
import {
  ArrowRight,
  Building2,
  FileCheck2,
  MessageSquareText,
  Newspaper,
  Search,
  Users,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "wouter";

const ALL_CATEGORIES = "all";

const SITUATION_SEARCHES = [
  { ko: "급여를 못 받았어요", en: "Unpaid wages", query: "급여 안 줘요" },
  { ko: "본드를 못 받았어요", en: "Bond not returned", query: "본드 안 돌려줘요" },
  { ko: "비자 기한이 급해요", en: "Urgent visa deadline", query: "비자 기한" },
  { ko: "병원·약이 필요해요", en: "Need health care", query: "병원 약" },
  { ko: "사기·해킹을 당했어요", en: "Scam or hacking", query: "사기 해킹" },
  { ko: "학교·육아가 막혔어요", en: "School or child care", query: "학교 차일드케어" },
] as const;

type CategoryFilter = TopicCategoryId | typeof ALL_CATEGORIES;

export default function Topics() {
  const [query, setQuery] = useState("");
  const [selectedCategory, setSelectedCategory] =
    useState<CategoryFilter>(ALL_CATEGORIES);
  const { locale, setLocale, isKorean } = useTopicLocale();

  const filteredTopics = useMemo(() => {
    const searchedTopics = searchTopics(query);
    return selectedCategory === ALL_CATEGORIES
      ? searchedTopics
      : searchedTopics.filter((topic) => topic.category === selectedCategory);
  }, [query, selectedCategory]);

  return (
    <>
      <Seo
        title={isKorean ? "동네 톡톡 생활문제 해결 가이드 — OZK" : "Neighbourhood Talk Life Guides — OZK"}
        description={
          isKorean
            ? "호주 생활에서 자주 마주치는 문제를 찾고, 단계별 정보와 관련 질문, 뉴스, 업체, 모임으로 이어가세요."
            : "Find practical Australian life guides, questions, updates, local experts, and meetups in one place."
        }
        path="/topics"
        keywords={[
          "호주 생활정보",
          "호주 한인 생활",
          "ABN",
          "운전면허",
          "렌트",
          "비자",
        ]}
      />

      <main className="min-h-screen bg-background">
        <section className="mx-auto w-full max-w-[1536px] px-4 py-8 md:px-6 md:py-10 lg:px-8 lg:py-12">
          <header className="max-w-6xl">
            <Badge variant="secondary" className="rounded-md">
              {isKorean ? "동네 톡톡 · 해결 가이드" : "Neighbourhood Talk · Life Guides"}
            </Badge>
            <h1 className="mt-4 text-3xl font-bold leading-tight text-foreground md:text-4xl">
              {isKorean
                ? "생활문제 해결 가이드"
                : "Find the Australian life help you need"}
            </h1>
            <p className="mt-4 text-pretty text-base leading-7 text-muted-foreground lg:whitespace-nowrap">
              {isKorean
                ? "기관명이나 제도 이름을 몰라도 괜찮습니다. 내 상황과 같은 문장을 고르면 지금 할 일, 공식 정보, 경험과 도움으로 이어집니다."
                : "Find practical Australian life guides, questions, updates, local experts, and meetups in one place."}
            </p>
          </header>

          <section
            aria-labelledby="life-problem-flow-heading"
            className="mt-8 rounded-xl border border-primary/20 bg-primary/5 p-4 md:p-5"
          >
            <div className="max-w-3xl">
              <h2 id="life-problem-flow-heading" className="text-lg font-bold text-foreground">
                {isKorean
                  ? "한 문제 안에서 해결 방법과 사람들의 경험을 함께 봅니다"
                  : "One problem brings guidance and community experience together"}
              </h2>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                {isKorean
                  ? "별도 메뉴를 오갈 필요 없이, 문제를 선택하면 다음 정보가 한 화면에서 연결됩니다."
                  : "Choose a problem once, then move through every kind of help from the same page."}
              </p>
            </div>
            <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
              {[
                { icon: FileCheck2, ko: "해결 가이드", en: "Verified guide" },
                { icon: MessageSquareText, ko: "질문·답변", en: "Questions" },
                { icon: Users, ko: "해결 경험", en: "Experiences" },
                { icon: Newspaper, ko: "관련 소식", en: "Updates" },
                { icon: Building2, ko: "도움받기", en: "Get help" },
              ].map((item) => (
                <div key={item.ko} className="flex items-center gap-2 rounded-lg bg-background px-3 py-2.5 text-sm font-medium text-foreground">
                  <item.icon className="h-4 w-4 shrink-0 text-primary" />
                  {isKorean ? item.ko : item.en}
                </div>
              ))}
            </div>
          </section>

          <section
            aria-labelledby="topic-directory-filter-heading"
            className="mt-8 rounded-lg border border-border bg-card p-4 md:p-5"
          >
            <h2 id="topic-directory-filter-heading" className="sr-only">
              Topic 검색과 카테고리 필터
            </h2>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                className="h-11 pl-9"
                placeholder={
                  isKorean
                    ? "예: 집주인이 본드를 안 돌려줘요, 신청이 거절됐어요"
                    : "e.g. landlord won't return my bond, my application was rejected"
                }
                aria-label={isKorean ? "Topic 검색" : "Search topics"}
              />
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant={
                  selectedCategory === ALL_CATEGORIES ? "default" : "outline"
                }
                onClick={() => setSelectedCategory(ALL_CATEGORIES)}
              >
                {isKorean ? "전체" : "All"}
              </Button>
              {TOPIC_CATEGORIES.map((category) => (
                <Button
                  key={category.id}
                  type="button"
                  size="sm"
                  variant={
                    selectedCategory === category.id ? "default" : "outline"
                  }
                  onClick={() => setSelectedCategory(category.id)}
                >
                  {getTopicCategoryLabel(category, locale)} {getTopicsByCategory(category.id).length}
                </Button>
              ))}
              <div className="ml-auto flex rounded-md border border-border p-1">
                <Button
                  type="button"
                  size="sm"
                  variant={locale === "ko" ? "default" : "ghost"}
                  onClick={() => setLocale("ko")}
                  className="h-8 px-3"
                >
                  한국어
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={locale === "en" ? "default" : "ghost"}
                  onClick={() => setLocale("en")}
                  className="h-8 px-3"
                >
                  English
                </Button>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-4">
              <span className="mr-1 text-xs font-semibold text-muted-foreground">
                {isKorean ? "많이 찾는 상황" : "Common situations"}
              </span>
              {SITUATION_SEARCHES.map((item) => (
                <Button
                  key={item.query}
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-8 rounded-full border border-border px-3 text-xs"
                  onClick={() => {
                    setSelectedCategory(ALL_CATEGORIES);
                    setQuery(item.query);
                  }}
                >
                  {isKorean ? item.ko : item.en}
                </Button>
              ))}
            </div>
          </section>

          <section
            aria-labelledby="topic-directory-heading"
            className="mt-8 space-y-4"
          >
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2
                  id="topic-directory-heading"
                  className="text-2xl font-bold text-foreground"
                >
                  {isKorean ? "내 상황과 가까운 문제" : "Problems closest to your situation"}
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  {isKorean
                    ? `지금 ${filteredTopics.length}개의 생활 주제를 볼 수 있습니다.`
                    : `${filteredTopics.length} life guides are ready to explore.`}
                </p>
              </div>
              <Link href="/questions/new">
                <Button variant="outline" className="gap-2">
                  {isKorean ? "질문 올리기" : "Ask a question"}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>

            {filteredTopics.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                {filteredTopics.map((topic) => {
                  const category = TOPIC_CATEGORIES.find(
                    (item) => item.id === topic.category,
                  );
                  const problem = getTopicProblem(topic.id);

                  return (
                    <Link
                      key={topic.id}
                      href={`/topics/${topic.id}`}
                      data-testid={`link-topic-directory-${topic.id}`}
                    >
                      <Card className="h-full rounded-lg transition-colors hover:bg-accent">
                        <CardContent className="flex h-full flex-col p-5">
                          <div className="flex flex-wrap items-center gap-2">
                            {category && (
                              <Badge variant="secondary" className="rounded-md">
                                {getTopicCategoryLabel(category, locale)}
                              </Badge>
                            )}
                            {problem && (
                              <Badge variant={problem.urgency === "high" ? "default" : "outline"} className="rounded-md">
                                {getTopicProblemUrgencyLabel(problem.urgency, locale)}
                              </Badge>
                            )}
                          </div>
                          <h3 className="mt-4 text-lg font-semibold text-foreground">
                            {getTopicLabel(topic, locale)}
                          </h3>
                          {getTopicSecondaryLabel(topic, locale) && (
                            <p className="mt-1 text-sm font-medium text-muted-foreground">
                              {getTopicSecondaryLabel(topic, locale)}
                            </p>
                          )}
                          <p className="mt-3 text-base font-semibold leading-6 text-foreground">
                            {problem?.problem[locale] ?? getTopicDescription(topic, locale)}
                          </p>
                          {problem && (
                            <div className="mt-4 rounded-md bg-muted p-3">
                              <p className="text-xs font-semibold text-muted-foreground">
                                {isKorean ? "지금 먼저 할 일" : "What to do first"}
                              </p>
                              <p className="mt-1 text-sm leading-5 text-foreground">
                                {problem.firstAction[locale]}
                              </p>
                            </div>
                          )}
                          <div className="mt-auto pt-5 text-sm font-medium text-primary">
                            {isKorean ? "내 상황 확인하기" : "Check my situation"}
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <Card className="rounded-lg border-dashed">
                <CardContent className="p-6 text-sm leading-6 text-muted-foreground">
                  {isKorean
                    ? "조건에 맞는 생활 주제가 없습니다. 검색어를 줄이거나 전체 카테고리에서 다시 찾아보세요."
                    : "No life guides match these filters. Try a broader search or switch back to all categories."}
                </CardContent>
              </Card>
            )}
          </section>

          <section className="mt-8 grid gap-3 sm:grid-cols-2" aria-label={isKorean ? "생활 질문과 경험 작성" : "Ask or share an experience"}>
            <Link href="/questions/new">
              <Card className="h-full rounded-lg transition-colors hover:bg-accent">
                <CardContent className="flex h-full items-center justify-between gap-4 p-5">
                  <div>
                    <p className="font-semibold text-foreground">
                      {isKorean ? "내 문제를 찾지 못했나요?" : "Could not find your problem?"}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {isKorean ? "상황을 질문으로 남겨 도움을 받아보세요." : "Describe your situation and ask the community."}
                    </p>
                  </div>
                  <Button variant="outline" className="shrink-0">
                    {isKorean ? "질문하기" : "Ask"}
                  </Button>
                </CardContent>
              </Card>
            </Link>
            <Link href="/experiences/new">
              <Card className="h-full rounded-lg transition-colors hover:bg-accent">
                <CardContent className="flex h-full items-center justify-between gap-4 p-5">
                  <div>
                    <p className="font-semibold text-foreground">
                      {isKorean ? "이미 해결한 경험이 있나요?" : "Have you solved a problem?"}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {isKorean ? "다음 사람을 위해 실제 과정과 결과를 나눠주세요." : "Share the steps and outcome for the next person."}
                    </p>
                  </div>
                  <Button variant="outline" className="shrink-0">
                    {isKorean ? "경험 공유" : "Share"}
                  </Button>
                </CardContent>
              </Card>
            </Link>
          </section>
        </section>
      </main>
    </>
  );
}
